/**
 * Idle Timeout Service
 *
 * This module provides functionality to automatically log out users
 * after a period of inactivity. The timeout duration is configurable
 * and defaults to 3 minutes.
 */

import { DEFAULT_IDEAL_TIMEOUT_MINUTES } from "@/config";
import { performIdleLogout } from "./logoutService";

/**
 * Configuration for idle timeout
 */
interface IdleTimeoutConfig {
  timeoutMinutes?: number;
  onIdleTimeout?: () => Promise<void>;
  excludeSelectors?: string[];
}

/**
 * Default configuration values
 */
const DEFAULT_CONFIG: Required<Omit<IdleTimeoutConfig, "excludeSelectors">> & {
  excludeSelectors: string[];
} = {
  timeoutMinutes: DEFAULT_IDEAL_TIMEOUT_MINUTES,
  onIdleTimeout: performIdleLogout,
  excludeSelectors: [],
};

/**
 * Class to manage idle timeout functionality
 */
export class IdleTimeoutManager {
  private timeoutId: NodeJS.Timeout | null = null;
  private config: Required<Omit<IdleTimeoutConfig, "excludeSelectors">> & {
    excludeSelectors: string[];
  };
  private isActive = false;
  private lastActivity = Date.now();

  /**
   * Events that reset the idle timer
   */
  private readonly activityEvents = [
    "mousedown",
    "mousemove",
    "keypress",
    "scroll",
    "touchstart",
    "click",
  ];

  constructor(config: IdleTimeoutConfig = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.handleActivity = this.handleActivity.bind(this);
  }

  /**
   * Starts the idle timeout monitoring
   */
  start(): void {
    if (this.isActive) {
      return;
    }

    this.isActive = true;
    this.lastActivity = Date.now();
    this.attachEventListeners();
    this.resetTimer();
  }

  /**
   * Stops the idle timeout monitoring
   */
  stop(): void {
    if (!this.isActive) {
      return;
    }

    this.isActive = false;
    this.detachEventListeners();
    this.clearTimer();
  }

  /**
   * Updates the timeout duration in minutes
   */
  setTimeoutMinutes(minutes: number): void {
    this.config.timeoutMinutes = minutes;
    if (this.isActive) {
      this.resetTimer();
    }
  }

  /**
   * Gets the current timeout duration in minutes
   */
  getTimeoutMinutes(): number {
    return this.config.timeoutMinutes;
  }

  /**
   * Gets the time remaining before idle timeout (in milliseconds)
   */
  getTimeRemaining(): number {
    const timeoutMs = this.config.timeoutMinutes * 60 * 1000;
    const elapsed = Date.now() - this.lastActivity;
    return Math.max(0, timeoutMs - elapsed);
  }

  /**
   * Manually triggers the idle timeout
   */
  async triggerTimeout(): Promise<void> {
    await this.config.onIdleTimeout();
  }

  /**
   * Attaches event listeners for activity detection
   */
  private attachEventListeners(): void {
    this.activityEvents.forEach((event) => {
      document.addEventListener(event, this.handleActivity, true);
    });
  }

  /**
   * Detaches event listeners
   */
  private detachEventListeners(): void {
    this.activityEvents.forEach((event) => {
      document.removeEventListener(event, this.handleActivity, true);
    });
  }

  /**
   * Handles user activity events
   */
  private handleActivity(event: Event): void {
    // Check if event should be excluded
    if (this.shouldExcludeEvent(event)) {
      return;
    }

    this.lastActivity = Date.now();
    this.resetTimer();
  }

  /**
   * Checks if an event should be excluded from activity detection
   */
  private shouldExcludeEvent(event: Event): boolean {
    if (this.config.excludeSelectors.length === 0) {
      return false;
    }

    const target = event.target as Element;
    if (!target) {
      return false;
    }

    return this.config.excludeSelectors.some((selector) => {
      try {
        return target.matches(selector) || target.closest(selector);
      } catch {
        return false;
      }
    });
  }

  /**
   * Resets the idle timer
   */
  private resetTimer(): void {
    this.clearTimer();

    const timeoutMs = this.config.timeoutMinutes * 60 * 1000;
    this.timeoutId = setTimeout(async () => {
      await this.config.onIdleTimeout();
    }, timeoutMs);
  }

  /**
   * Clears the current timer
   */
  private clearTimer(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }
}

/**
 * Global instance of the idle timeout manager
 */
let globalIdleManager: IdleTimeoutManager | null = null;

/**
 * Initializes the global idle timeout manager
 */
export const initializeIdleTimeout = (
  config?: IdleTimeoutConfig,
): IdleTimeoutManager => {
  if (globalIdleManager) {
    globalIdleManager.stop();
  }

  globalIdleManager = new IdleTimeoutManager(config);
  globalIdleManager.start();

  return globalIdleManager;
};

/**
 * Gets the global idle timeout manager instance
 */
export const getIdleTimeoutManager = (): IdleTimeoutManager | null => {
  return globalIdleManager;
};

/**
 * Stops the global idle timeout manager
 */
export const stopIdleTimeout = (): void => {
  if (globalIdleManager) {
    globalIdleManager.stop();
    globalIdleManager = null;
  }
};
